/**
 * La bulle d'un point sur la carte de la LISTE des formateurs.
 *
 * Elle répond aux quatre questions qu'on se pose en cherchant quelqu'un sur une
 * carte — qui est-ce, où est-il, à quelle distance du point visé, à quel prix —
 * puis mène à la fiche, seul endroit où l'on peut agir.
 *
 * CE QU'ELLE NE DIT PAS, ET POURQUOI. Le statut n'y figure pas : la couleur du
 * point le dit déjà, et il mangerait la place des deux chiffres qu'on vient
 * réellement y chercher. Le nombre de départements couverts est parti pour la
 * même raison — c'est une donnée de fiche, pas de carte : on ne choisit personne
 * sur sa mobilité déclarée quand on a sa distance réelle sous les yeux.
 *
 * POURQUOI UN MODULE À PART. Ce n'est que du HTML en chaîne, sans état ni
 * réactivité : sorti du composant, il laisse `FormateursMap` à ce qu'il est —
 * une carte, sa légende et son trajet — et chaque ligne de la bulle se lit
 * seule. Le gabarit commun, lui, reste dans `../shared/map-bulle-formateur.js`.
 */
import { FORMATEUR_TYPE_LABELS } from '../../constants/formateur.js'
import { formateurDisplayName as nomAffiche } from '../../utils/formateurDisplay.js'
import {
  badgeType,
  bulleFormateur,
  echapper,
  etoilesBulle,
  lienFiche,
  ligneBulle,
  mentionDiscrete,
} from '../shared/map-bulle-formateur.js'

/** La commune et son département ; à défaut de commune, le code postal seul. */
function ligneLieu(f) {
  const lieu = f.ville
    ? `${echapper(f.ville)}${f.departement ? ` ${mentionDiscrete(`(${echapper(f.departement)})`)}` : ''}`
    : (f.codePostal ? echapper(f.codePostal) : null)
  return lieu && ligneBulle('Lieu', lieu)
}

/**
 * Le trajet se calcule APRÈS l'ouverture (cf. `FormateursMap.afficherTrajet`) :
 * la bulle réserve sa ligne, et dit qu'elle cherche. Sans point de référence ou
 * sans position, il n'y a rien à calculer — pas de ligne.
 */
function ligneTrajet(f, avecTrajet) {
  if (!avecTrajet || !f.coords) return null
  return ligneBulle('Trajet', `<span data-trajet="${echapper(f.id)}" style="font-weight:500">Calcul du trajet…</span>`)
}

function ligneTarif(f) {
  if (f.tarifJournee == null) return null
  const tarif = `${f.tarifJournee} € HT / jour`
  return ligneBulle('Tarif', `<span style="font-weight:500;color:#0f172a">${echapper(tarif)}</span>`)
}

function ligneCompetences(f) {
  if (!f.nbCompetences) return null
  const competences = `${f.nbCompetences} compétence${f.nbCompetences > 1 ? 's' : ''}`
  return ligneBulle('Compétences', `<span style="font-weight:500">${echapper(competences)}</span>`)
}

function ligneNote(f) {
  const etoiles = etoilesBulle(f.notationAlertis)
  return etoiles && ligneBulle('Note', etoiles)
}

/** La réserve d'un point qu'on n'a pu poser qu'au centre de son département. */
function mentionApproximative(f) {
  if (f.coordsPrecision !== 'departement') return ''
  return '<div style="margin-top:8px;font-size:11px;color:#94a3b8">Position approximative : centre du département</div>'
}

/**
 * Le HTML complet de la bulle.
 *
 * @param {object}  f           Le formateur du point.
 * @param {boolean} avecTrajet  Un point de référence est posé : la bulle réserve la ligne du trajet.
 */
export function bulleFormateurListe(f, { avecTrajet = false } = {}) {
  const lignes = [ligneLieu(f), ligneTrajet(f, avecTrajet), ligneTarif(f), ligneCompetences(f), ligneNote(f)]
    .filter(Boolean)
    .join('')

  return bulleFormateur({
    nom: nomAffiche(f),
    badge: badgeType(f.type, FORMATEUR_TYPE_LABELS[f.type] || ''),
    lignes,
    pied: mentionApproximative(f) + lienFiche(f),
  })
}
